import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Upload } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/lib/designTokens";
import { ACCOUNT_NAV, ADMIN_NAV, NAV_GROUPS } from "@/lib/navigationConfig";
import { isNavItemActive, navLinkClassName } from "@/lib/navUtils";
import { cn } from "@/lib/utils";

export const MobileNavigation = ({
  open,
  onOpenChange,
  onUploadClick,
  coachPendingCount = 0,
}) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const showAdmin = user?.is_admin === true;

  useEffect(() => {
    if (open) onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, onOpenChange]);

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-50 bg-black/60 md:hidden transition-opacity",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        aria-hidden={!open}
        onClick={() => onOpenChange(false)}
        data-testid="mobile-nav-backdrop"
      />

      <nav
        id="mobile-navigation"
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-[min(100%,20rem)] md:hidden flex flex-col border-r border-dark-border bg-dark-surface-1 shadow-xl transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        aria-label="Mobile primary"
        aria-hidden={!open}
        data-testid="mobile-navigation"
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-dark-border">
          <div>
            <div className="font-heading text-xl tracking-wider uppercase">{BRAND.productName}</div>
            <div className="text-[10px] tracking-[0.12em] text-dark-text-muted uppercase">{BRAND.companyName}</div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-icon-app focus-visible:outline-none"
            aria-label="Close navigation menu"
            data-testid="mobile-nav-close"
          >
            <span className="text-xl leading-none" aria-hidden="true">
              ×
            </span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
          {NAV_GROUPS.map((group) => (
            <div key={group.id}>
              <div className="px-2 mb-2 text-[10px] tracking-[0.18em] uppercase text-dark-text-muted font-semibold">
                {group.label}
              </div>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const active = isNavItemActive(location.pathname, item.to, { end: item.end });
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => onOpenChange(false)}
                      className={navLinkClassName(active)}
                      data-testid={`mobile-${item.testId}`}
                      aria-current={active ? "page" : undefined}
                    >
                      <Icon className="w-5 h-5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                      <span className="flex-1">{item.label}</span>
                      {item.to === "/coach" && coachPendingCount > 0 && (
                        <span
                          className="inline-flex min-w-[1.125rem] h-[1.125rem] items-center justify-center rounded-full bg-brand-primary px-1 text-[10px] font-semibold text-white"
                          data-testid="mobile-nav-coach-pending-badge"
                          aria-label={`${coachPendingCount} pending coach action`}
                        >
                          {coachPendingCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          <div>
            <div className="px-2 mb-2 text-[10px] tracking-[0.18em] uppercase text-dark-text-muted font-semibold">
              Account
            </div>
            <div className="space-y-1">
            <Link
              to={ACCOUNT_NAV.to}
              onClick={() => onOpenChange(false)}
              className={navLinkClassName(isNavItemActive(location.pathname, ACCOUNT_NAV.to))}
              data-testid={`mobile-${ACCOUNT_NAV.testId}`}
              aria-current={isNavItemActive(location.pathname, ACCOUNT_NAV.to) ? "page" : undefined}
            >
              <ACCOUNT_NAV.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              {ACCOUNT_NAV.label}
            </Link>
            {showAdmin ? (
              <Link
                to={ADMIN_NAV.to}
                onClick={() => onOpenChange(false)}
                className={navLinkClassName(isNavItemActive(location.pathname, ADMIN_NAV.to))}
                data-testid={`mobile-${ADMIN_NAV.testId}`}
                aria-current={isNavItemActive(location.pathname, ADMIN_NAV.to) ? "page" : undefined}
              >
                <ADMIN_NAV.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                {ADMIN_NAV.label}
              </Link>
            ) : null}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-dark-border space-y-2">
          <button
            type="button"
            onClick={() => {
              onOpenChange(false);
              onUploadClick?.();
            }}
            className="btn-primary-app w-full min-h-[44px] rounded-md text-xs tracking-[0.15em] uppercase"
            data-testid="mobile-upload-button"
          >
            <Upload className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
            Upload activity
          </button>
          <button
            type="button"
            onClick={() => {
              onOpenChange(false);
              logout();
            }}
            className="w-full min-h-[44px] rounded-md text-xs tracking-[0.15em] uppercase inline-flex items-center justify-center gap-2 border border-dark-border text-analytics-red"
            data-testid="mobile-logout"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
            Log out
          </button>
        </div>
      </nav>
    </>
  );
};
